/**
 * Shared normalization for the analytics params that describe *where* a
 * product-URL import came from.
 *
 * Two values ship on every `ai_product_import_*` event:
 *
 *   - `source_domain` — the listing's hostname, lowercased with a leading
 *     `www.` stripped, so GA4 groups `www.westelm.com` and `westelm.com`
 *     as one retailer.
 *   - `product_url` — the listing's `origin + pathname`, with the query
 *     string and fragment removed (they carry tracking params / PII and
 *     bloat the value) and truncated to GA4's 100-character parameter
 *     limit.
 *
 * Both helpers are total: unparseable input returns `undefined` rather
 * than throwing, so a malformed paste can never crash the import flow or
 * the event. Callers omit the param when the result is `undefined`.
 */

/** GA4 caps an event parameter *value* at 100 characters. */
export const GA4_PARAM_VALUE_MAX = 100;

/**
 * Lowercase a hostname and strip a single leading `www.`.
 *
 * Use this on the hostname the server already resolved (the `sourceDomain`
 * field on the import response) so the client doesn't re-parse the URL.
 * Returns `undefined` for empty / non-string input.
 */
export function normalizeSourceDomain(
  hostname: string | null | undefined
): string | undefined {
  if (typeof hostname !== "string") return undefined;
  const trimmed = hostname.trim().toLowerCase();
  if (trimmed.length === 0) return undefined;
  return trimmed.replace(/^www\./, "");
}

/**
 * Derive {@link normalizeSourceDomain} from a full URL string (the raw
 * text the user pasted). Returns `undefined` when the string isn't a
 * parseable URL.
 */
export function sourceDomainForAnalytics(
  input: string | null | undefined
): string | undefined {
  const url = parseUrl(input);
  if (!url) return undefined;
  return normalizeSourceDomain(url.hostname);
}

/**
 * `origin + pathname` of a URL, without the query string or fragment,
 * truncated to {@link GA4_PARAM_VALUE_MAX}. Returns `undefined` when the
 * input isn't a parseable URL.
 */
export function productUrlForAnalytics(
  input: string | null | undefined
): string | undefined {
  const url = parseUrl(input);
  if (!url) return undefined;
  const clean = `${url.origin}${url.pathname}`;
  return clean.slice(0, GA4_PARAM_VALUE_MAX);
}

function parseUrl(input: string | null | undefined): URL | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  try {
    return new URL(trimmed);
  } catch {
    return null;
  }
}
